import {DropdownOptionProps} from '@wix/wix-ui-tpa';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import classNames from 'classnames';
import React from 'react';
import {UserInputType} from '../../../constants';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {RenderContext, withRenderContext, type WithRenderContextProps} from '../../../providers/renderContext';
import {SPECS} from '../../../specs';
import {LazyDropdown} from '../../lazyComponents/lazyWUTComponents';
import {scrollAndFocusUserInput} from '../utils/scrollAndFocusUserInput';
import s from './OptionsDropdown.scss';
import {st, classes as stylableClasses} from './OptionsDropdown.st.css';

export interface OptionsDropdownProps extends ProvidedGlobalProps, IProvidedTranslationProps, WithRenderContextProps {
  optionIndex: number;
  optionalModifiersEnabled: boolean;
  isRequired?: boolean;
  showErrors?: boolean;
}

const NONE_SELECTION_ID = '__none__';
const NO_SELECTION_ID = '__no_selection__';

export enum OptionsDropdownDataHook {
  OptionsDropdownContainer = 'options-dropdown-container',
  OptionsDropdown = 'options-dropdown',
  OptionsDropdownTitle = 'options-dropdown-title',
}

class OptionsDropdownComponent extends React.Component<OptionsDropdownProps> {
  private readonly dropdownContainerRef = React.createRef<HTMLDivElement>();

  public componentDidUpdate(prevProps: OptionsDropdownProps) {
    const {
      optionIndex,
      globals: {scrollAndFocusInputTarget, isQuickView},
    } = this.props;

    if (prevProps.globals.scrollAndFocusInputTarget !== scrollAndFocusInputTarget) {
      const isScrollAndFocusInputTarget =
        UserInputType.Selection === scrollAndFocusInputTarget?.inputType &&
        optionIndex === scrollAndFocusInputTarget?.index;

      if (isScrollAndFocusInputTarget && this.dropdownContainerRef.current) {
        scrollAndFocusUserInput(UserInputType.Selection, this.dropdownContainerRef.current, isQuickView);
      }
    }
  }

  private readonly handleChange = ({id: selectionId}: DropdownOptionProps) => {
    const {
      optionIndex,
      renderContext,
      optionalModifiersEnabled,
      globals: {handleUserInput, validateUserInputsChanges},
    } = this.props;

    let nextSelection;
    if (optionalModifiersEnabled && selectionId === NONE_SELECTION_ID) {
      nextSelection = undefined;
    } else {
      nextSelection = this.productOption.selections.find(({id}) => id.toString() === selectionId);
    }

    handleUserInput(UserInputType.Selection, nextSelection, optionIndex, renderContext === RenderContext.StickyDrawer);

    validateUserInputsChanges();
  };

  private readonly isSelectionAvailable = (isSelectable: boolean): boolean => {
    const {
      globals: {isBackInStockEnabled},
    } = this.props;

    return isBackInStockEnabled ? !isSelectable : false;
  };

  private get activeSelection() {
    const {
      optionIndex,
      globals: {userInputs},
    } = this.props;
    return userInputs[UserInputType.Selection][optionIndex];
  }

  private get productOption() {
    const {
      optionIndex,
      globals: {product},
    } = this.props;

    return product.options[optionIndex];
  }

  private get isGroupedByOption(): boolean {
    const {productGroup, isCombinedListing} = this.props.globals;
    return !!isCombinedListing && this.productOption.id === productGroup?.groupingCustomizationId;
  }

  private get dropdownOptions(): DropdownOptionProps[] {
    const {
      globals: {
        variantInfo,
        isBackInStockEnabled,
        experiments: {showHiddenVariants},
      },
      optionalModifiersEnabled,
      isRequired,
      t,
    } = this.props;

    const optionAvailability = variantInfo.selectionsAvailability[this.productOption.id];

    const selections = this.productOption.selections
      .filter((selection) => this.isGroupedByOption || showHiddenVariants || optionAvailability[selection.id].isVisible)
      .map((selection) => {
        const {isVisible, isSelectable: isInStock} = optionAvailability[selection.id];
        const option = {id: selection.id.toString(), value: selection.description};

        if (this.isGroupedByOption) {
          return {...option, isSelectable: true, unavailable: false};
        }

        if (showHiddenVariants) {
          return {...option, isSelectable: true, unavailable: !isVisible || !isInStock};
        }

        return {
          ...option,
          isSelectable: isBackInStockEnabled ? true : isInStock,
          unavailable: this.isSelectionAvailable(isInStock),
        };
      });

    if (optionalModifiersEnabled && !isRequired) {
      const noneSelectionOption = {
        id: NONE_SELECTION_ID,
        value: t('NONE'),
        isSelectable: true,
      };
      return [noneSelectionOption, ...selections];
    }

    return selections;
  }

  public state: Readonly<{labelForId: string}> = {
    labelForId: undefined,
  };

  private getPresentationProps({
    title,
    selectedId,
    isStickyAddToCartEnabled,
  }: {
    title: string;
    selectedId?: string;
    isStickyAddToCartEnabled: boolean;
  }) {
    const {optionalModifiersEnabled, isRequired, t} = this.props;
    const initialSelectedId = (this.activeSelection?.id || '').toString();

    if (optionalModifiersEnabled) {
      const label = isRequired ? title : `${title} (${t('OPTIONAL')})`;
      const accessibilityProps = {'aria-required': !!isRequired, showAsterisk: !!isRequired};
      if (!isRequired) {
        return {
          label,
          selectionProps: {selectedId: selectedId ?? NO_SELECTION_ID},
          accessibilityProps,
        };
      }
      if (isStickyAddToCartEnabled) {
        return {label, selectionProps: {selectedId}, accessibilityProps};
      }
      return {label, selectionProps: {initialSelectedId}, accessibilityProps};
    } else {
      const accessibilityProps = {'aria-required': true, showAsterisk: true};
      if (isStickyAddToCartEnabled) {
        return {label: title, selectionProps: {selectedId}, accessibilityProps};
      }
      return {label: title, selectionProps: {initialSelectedId}, accessibilityProps};
    }
  }

  public render(): JSX.Element {
    const {
      t,
      optionIndex,
      showErrors = true,
      globals: {isProductSubmitted, userInputErrors, isQuickView, useExperiments, isSSR},
      renderContext,
    } = this.props;

    const {title, id: dropdownId} = this.productOption;
    const isStickyAddToCartEnabled = useExperiments.enabled(SPECS.StoresStickyAddToCart);
    const isFixDropdownMobileSsr = useExperiments.enabled(SPECS.FixProductPageDropdownMobileSsr);
    const selectedId = this.activeSelection?.id?.toString();
    const mobileNativeSelect = isFixDropdownMobileSsr ? !isSSR : true;

    const {label, selectionProps, accessibilityProps} = this.getPresentationProps({
      title,
      selectedId,
      isStickyAddToCartEnabled,
    });

    const errorProps =
      showErrors || !isStickyAddToCartEnabled
        ? {
            newErrorMessage: true,
            error: isProductSubmitted && userInputErrors[UserInputType.Selection][optionIndex],
            errorMessage: t('SELECT_OPTION_WARNING', {optionTitle: title}),
          }
        : {};

    const isStickyDrawer = isStickyAddToCartEnabled && renderContext === RenderContext.StickyDrawer;

    return (
      <div data-hook={OptionsDropdownDataHook.OptionsDropdownContainer} ref={this.dropdownContainerRef}>
        <LazyDropdown
          className={classNames(
            st(stylableClasses.stylableRoot, {isQuickView, stickyDrawer: isStickyDrawer}),
            s.dropdownListNewStyling
          )}
          dropdownId={dropdownId}
          label={label}
          aria-label={label}
          options={this.dropdownOptions}
          onChange={this.handleChange}
          placeholder={t('SELECT_PLACE_HOLDER')}
          {...selectionProps}
          data-hook={OptionsDropdownDataHook.OptionsDropdown}
          mobileNativeSelect={mobileNativeSelect}
          {...errorProps}
          {...accessibilityProps}
        />
      </div>
    );
  }
}

export const OptionsDropdown = withGlobalProps(
  withTranslations('globals.texts')(withRenderContext(OptionsDropdownComponent))
);
